import { useDisclosure } from '@mantine/hooks';
import { useMemo, useState } from 'react';
import { useOrganizationUsers, usePermissionGroups } from '../../../../api/rbac-client/rbac-client.hooks';
import { useOrganizationId } from '../../../../helpers/auth-helpers/auth.hooks';
import { AddUserModal } from './add-user-modal';
import { EditGroupsModal } from './edit-groups-modal';
import { GroupsContent } from './groups-content';
import { useAllGroupMembers } from './groups-tab.helpers';
import { GroupsList } from './groups-list';
import { GroupsContainer } from './groups-tab.styles';

export function GroupsTab() {
  const organizationId = useOrganizationId();
  const { data: groups = [], query: groupsQuery } = usePermissionGroups(organizationId);
  const { data: orgUsers = [], query: orgUsersQuery } = useOrganizationUsers(organizationId);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [addUserOpened, { open: openAddUser, close: closeAddUser }] = useDisclosure(false);
  const [editGroupsOpened, { open: openEditGroups, close: closeEditGroups }] = useDisclosure(false);

  const groupIds = useMemo(() => groups.map((g) => g.id), [groups]);
  const { membersByGroupId, isLoading: membersLoading } = useAllGroupMembers(groupIds);
  const isLoading = groupsQuery.isLoading || orgUsersQuery.isLoading || membersLoading;
  const error = groupsQuery.error ?? orgUsersQuery.error;

  if (error) {
    return <GroupsContainer>Failed to load groups data.</GroupsContainer>;
  }

  return (
    <GroupsContainer>
      <GroupsList groups={groups} selectedGroupId={selectedGroupId} onSelectGroup={setSelectedGroupId} />
      <GroupsContent
        groups={groups}
        selectedGroupId={selectedGroupId}
        membersByGroupId={membersByGroupId}
        isLoading={isLoading}
        onOpenAddUser={openAddUser}
        onOpenEditGroups={openEditGroups}
      />
      {organizationId && (
        <>
          <AddUserModal
            opened={addUserOpened}
            onClose={closeAddUser}
            organizationId={organizationId}
            orgUsers={orgUsers}
            groups={groups}
          />
          <EditGroupsModal
            opened={editGroupsOpened}
            onClose={closeEditGroups}
            organizationId={organizationId}
            groups={groups}
            orgUsers={orgUsers}
            membersByGroupId={membersByGroupId}
          />
        </>
      )}
    </GroupsContainer>
  );
}
